import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { listingUnitLabel } from '../../util/editListingHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;
const MIN_LENGTH_FOR_LONG_WORDS = 10;

// Show all-in price (base + 15% customer fee)
const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const allInAmount = Math.round(price.amount * 1.15);
    const allInPrice = new Money(allInAmount, price.currency);
    const formattedPrice = formatMoney(intl, allInPrice);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: string or null for the price block content (same translation as used in cardAriaLabel when shown)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  // Quote-mode listings (publicData.pricingMode === 'quote') store a
  // floor price as the listing's `price` attribute (set in
  // EditListingDetailsPanel from publicData.startingPrice). The card
  // shows it with the same "Starting at $X" wording the multi-variant
  // path uses — both communicate "this is a floor, not a fixed rate".
  const isQuoteMode = publicData?.pricingMode === 'quote';

  const priceMessageId =
    hasMultiplePriceVariants || isQuoteMode
      ? 'ListingCard.priceStartingFrom'
      : 'ListingCard.price';

  // Price quantifier comes from the canonical mapping (listingUnitLabel),
  // never the booking-process unitType — that's what printed "/night" on
  // kits. Quote-mode → host's chosen basis; fixed/tiered → "per day".
  const unitLabel = listingUnitLabel(publicData);

  // Single formatted price line (amount + per-unit if applicable); used for both card aria and price block
  // Clean price: remove ".00" cents for whole-dollar amounts
  const cleanPrice = typeof formattedPrice === 'string' ? formattedPrice.replace(/\.00$/, '') : formattedPrice;
  const priceValue = <span className={css.priceValue}>{cleanPrice}</span>;
  const pricePerUnit = unitLabel ? (
    <span className={css.perUnit}> {unitLabel}</span>
  ) : (
    ''
  );
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const cardAriaLabel =
    priceMessage.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessage }
        )
      : title;

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
  };
};
